import { getDefaultAccessors } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { CustomViewSortConfigItem } from 'src/graphql/entities/custom-view/custom-view.types';

// The attributes listed in the ConversationAttribute are the only identifiers that will be stored in the database
// to track the user's selection. These attributes should remain abstract and not be
// directly tied to the database schema. Instead, they serve as logical names for the attributes,
// which can be matched to their corresponding configuration and settings in the system.
export const ConversationAttribute = {
  id: 'id',
  conversationUuid: 'conversationUuid',
  conversationUrgency: 'conversationUrgency',
  conversationStatus: 'conversationStatus',
  subject: 'subject',
  type: 'type',
  fullSiteAddress: 'fullSiteAddress',
  requesterFullName: 'requesterFullName',
  requesterEmail: 'requesterEmail',
  requesterPhone: 'requesterPhone',
  requesterType: 'requesterType',
  serviceLevel: 'serviceLevel',
  lastMessageReceivedAt: 'lastMessageReceivedAt',
  unseenCount: 'unseenCount',
  primaryClientName: 'primaryClientName',
  assigneeGroup: 'assigneeGroup',
  assignee: 'assignee',
  assigneeFullName: 'assigneeFullName',
  snoozeUntil: 'snoozeUntil',
  partnerName: 'partnerName',
  timeZone: 'timeZone',
  partnerId: 'partnerId',
  requesterId: 'requesterId',
  createdAt: 'createdAt',
  conversationStatusUpdatedAt: 'conversationStatusUpdatedAt',
  addressId: 'addressId',
  addressCity: 'addressCity',
  addressZip: 'addressZip',
  addressCppExpDate: 'addressCppExpDate',
  membershipRegistered: 'membershipRegistered',
  addressName: 'addressName',
  address: 'address',
  conversationAddressLabel: 'conversationAddressLabel',
  clientTosAccepted: 'clientTosAccepted',
  clientSegment: 'clientSegment',
  label: 'label',
  sla: 'sla',
  userParticipant: 'userParticipant',
  deleted: 'deleted',
  deletedAt: 'deletedAt',
} as const;

export type ConversationAttributeType =
  (typeof ConversationAttribute)[keyof typeof ConversationAttribute];

export const ConversationAttributeAccessors: Record<
  ConversationAttributeType,
  CustomViewSortConfigItem
> = {
  [ConversationAttribute.id]: {
    sort: ['partnerId', 'workspaceConversationId'],
    filter: ConversationAttribute.id,
    display: 'friendlyId',
  },
  [ConversationAttribute.conversationUuid]: {
    filter: 'conversationId',
    display: 'conversationId',
  },
  [ConversationAttribute.conversationUrgency]: {
    sort: 'urgency/sortOrder',
    filter: 'conversationUrgencyId',
    display: 'urgency',
  },
  [ConversationAttribute.conversationStatus]: {
    sort: 'status/sortOrder',
    filter: 'conversationStatusId',
    display: 'conversationStatusId',
  },
  [ConversationAttribute.subject]: getDefaultAccessors('subject'),
  [ConversationAttribute.type]: getDefaultAccessors('type'),
  [ConversationAttribute.fullSiteAddress]: {
    sort: ['associatedAddresses/address1', 'associatedAddresses/address2'],
    filter: ConversationAttribute.fullSiteAddress,
    display: 'address.fullAddress',
  },
  [ConversationAttribute.serviceLevel]: {
    sort: 'associatedAddresses/platformSlaId',
    filter: 'associatedAddresses/platformSlaId',
    display: 'address.platformSla.displayName',
  },
  [ConversationAttribute.lastMessageReceivedAt]: getDefaultAccessors('lastMessageReceivedAt'),
  [ConversationAttribute.unseenCount]: {
    filter: ConversationAttribute.unseenCount,
    display: 'unseenCount',
  },
  [ConversationAttribute.primaryClientName]: {
    sort: [
      'associatedAddresses/primaryClient/firstName',
      'associatedAddresses/primaryClient/lastName',
    ],
    filter: 'associatedAddresses/primaryClient/firstName',
    display: 'address.primaryClient.fullName',
  },
  [ConversationAttribute.requesterFullName]: {
    sort: ['requester/platformUser/firstName', 'requester/client/firstName'],
    filter: ConversationAttribute.requesterFullName,
    display: 'requesterFullName',
  },
  [ConversationAttribute.requesterEmail]: {
    sort: ['requester/platformUser/email', 'requester/client/primaryEmail/value'],
    filter: ConversationAttribute.requesterEmail,
    display: 'requesterData.email',
  },
  [ConversationAttribute.requesterPhone]: {
    sort: ['requester/platformUser/phoneNumber', 'requester/client/primaryPhoneNumber/value'],
    filter: ConversationAttribute.requesterPhone,
    display: 'requesterData.phoneNumber',
  },
  [ConversationAttribute.requesterType]: {
    sort: 'requester/participantEntity',
    filter: ConversationAttribute.requesterType,
    display: 'requesterData.participantEntity',
  },
  [ConversationAttribute.assigneeGroup]: {
    sort: 'conversationAssigneeGroup/name',
    filter: 'conversationAssigneeGroupId',
    display: 'conversationAssigneeGroup.name',
  },
  [ConversationAttribute.assigneeFullName]: {
    sort: ['assignee/firstName', 'assignee/lastName'],
    filter: ConversationAttribute.assigneeFullName,
    display: 'assigneeFullName',
  },
  [ConversationAttribute.snoozeUntil]: getDefaultAccessors('snoozeUntil'),
  [ConversationAttribute.partnerName]: getDefaultAccessors('partner.businessName'),
  [ConversationAttribute.timeZone]: getDefaultAccessors('partner.timeZone'),
  [ConversationAttribute.partnerId]: getDefaultAccessors('partner.id'),
  [ConversationAttribute.requesterId]: getDefaultAccessors('requester.participantIdentity'),
  [ConversationAttribute.createdAt]: getDefaultAccessors('createdAt'),
  [ConversationAttribute.deletedAt]: getDefaultAccessors('deletedAt'),
  [ConversationAttribute.conversationStatusUpdatedAt]: getDefaultAccessors(
    'conversationStatusUpdatedAt',
  ),
  [ConversationAttribute.addressId]: {
    sort: 'associatedAddresses/id',
    filter: 'associatedAddresses/id',
    display: 'address.id',
  },
  [ConversationAttribute.addressCity]: {
    sort: 'associatedAddresses/city',
    filter: 'associatedAddresses/city',
    display: 'address.city',
  },
  [ConversationAttribute.addressZip]: {
    sort: 'associatedAddresses/zip',
    filter: 'associatedAddresses/zip',
    display: 'address.zip',
  },
  [ConversationAttribute.addressCppExpDate]: {
    sort: 'associatedAddresses/cppExpDate',
    filter: 'associatedAddresses/cppExpDate',
    display: 'address.cppExpDate',
  },
  [ConversationAttribute.addressName]: {
    sort: 'associatedAddresses/businessName',
    filter: 'associatedAddresses/businessName',
    display: 'address.businessName',
  },
  [ConversationAttribute.address]: {
    sort: 'associatedAddresses/address1',
    filter: 'associatedAddresses/address1',
    display: 'address.address',
  },
  [ConversationAttribute.clientTosAccepted]: {
    sort: 'associatedAddresses/tosAccepted',
    filter: 'associatedAddresses/tosAccepted',
    display: 'address.tosAccepted',
  },
  [ConversationAttribute.clientSegment]: {
    sort: 'associatedAddresses/brand/name',
    filter: 'associatedAddresses/brand/id',
    display: 'address.brand.name',
  },
  [ConversationAttribute.assignee]: {
    sort: 'assignee/firstName',
    filter: 'conversationAssigneeId',
    display: 'address.brand.name',
  },
  [ConversationAttribute.membershipRegistered]: {
    sort: 'associatedAddresses/creditCardProvided',
    filter: 'associatedAddresses/creditCardProvided',
    display: 'address.creditCardProvided',
  },
  [ConversationAttribute.label]: {
    filter: 'labelAssignments/labelId',
    display: 'labelAssignments',
  },
  [ConversationAttribute.sla]: getDefaultAccessors('associatedAddresses/sla'),
  [ConversationAttribute.conversationAddressLabel]: {
    sort: 'associatedAddresses/labelAssignments/label/displayName',
    filter: 'associatedAddresses/labelAssignments/labelId',
    display: ConversationAttribute.conversationAddressLabel,
  },
  [ConversationAttribute.userParticipant]: {
    sort: ['participations/channel/conversationParticipant/participantIdentity'],
    filter: 'participations/channel/conversationParticipant/participantIdentity',
    display: '', // left it empty as it isn't shown on Custom View table.
  },
  [ConversationAttribute.deleted]: {
    filter: 'deletedAt',
    display: 'deletedAt',
  },
};
